import {animate, group, query, style, transition, trigger} from '@angular/animations';
import {CommonModule, TitleCasePipe} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {Observable, Subject, Subscription, take} from 'rxjs';
import {ChallengeBuilderProject} from 'src/app/core/model/project';
import {
  GetChallengeBuilderProjectAction,
  SetBackLoadingAction,
  SetChallengeBuilderLoadingAction,
  SetNextLoadingAction,
  ToggleAiAction,
} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {
  selectChallengeBuilderBackLoading,
  selectChallengeBuilderLoading,
  selectChallengeBuilderNextLoading,
  selectChallengeBuilderProject,
  selectChallengeNextValid,
} from 'src/app/core/store/challenge-builder/challenge-builder.selector';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphLoadingModule} from 'src/app/shared/design/oph-loading/oph-loading.module';
import {OphSlideToggleComponent} from 'src/app/shared/design/oph-slide-toggle/oph-slide-toggle.component';
import {OphXButtonComponent} from 'src/app/shared/design/oph-x-button/oph-x-button.component';
import {CbGoalComponent} from './cb-goal/cb-goal.component';
import {CbMetricsComponent} from './cb-metrics/cb-metrics.component';
import {CbObjectivesComponent} from './cb-objectives/cb-objectives.component';
import {CbOverviewComponent} from './cb-overview/cb-overview.component';
import {CbScheduleComponent} from './cb-schedule/cb-schedule.component';
import {CbTargetsComponent} from './cb-targets/cb-targets.component';
import {CbTasksComponent} from './cb-tasks/cb-tasks.component';
import {
  CHALLENGE_BUILDER_STEP_MAP,
  CHALLENGE_BUILDER_STEPS,
  ChallengeBuilderSteps,
  ChallengeStep,
} from './challenge-builder-data';
import {CbSidebarComponent} from './shared/cb-sidebar/cb-sidebar.component';
import {GetProjectsAction} from 'src/app/core/store/projects/projects.action';

// Shared animation duration in milliseconds
const ANIMATION_DURATION_MS = 400;

@Component({
  selector: 'challenge-builder',
  standalone: true,
  imports: [
    CommonModule,
    OphButtonModule,
    OphIconModule,
    OphLoadingModule,
    OphSlideToggleComponent,
    OphXButtonComponent,
    TitleCasePipe,
    CbGoalComponent,
    CbObjectivesComponent,
    CbTasksComponent,
    CbMetricsComponent,
    CbTargetsComponent,
    CbScheduleComponent,
    CbOverviewComponent,
    CbSidebarComponent,
  ],
  templateUrl: './challenge-builder.component.html',
  styleUrl: './challenge-builder.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('sectionSlide', [
      transition(':increment', [
        style({opacity: 0, transform: 'translateX(100%)'}),
        animate(`${ANIMATION_DURATION_MS}ms ease-out`, style({opacity: 1, transform: 'translateX(0)'})),
      ]),
      transition(':decrement', [
        style({opacity: 0, transform: 'translateX(-100%)'}),
        animate(`${ANIMATION_DURATION_MS}ms ease-out`, style({opacity: 1, transform: 'translateX(0)'})),
      ]),
    ]),
    trigger('contentSlide', [
      transition(':increment', [
        group([
          query(
            ':leave',
            [
              style({position: 'absolute', width: '100%', opacity: 1, transform: 'translateX(0)'}),
              animate(`${ANIMATION_DURATION_MS}ms ease-out`, style({opacity: 0, transform: 'translateX(-100%)'})),
            ],
            {optional: true}
          ),
          query(
            ':enter',
            [
              style({opacity: 0, transform: 'translateX(100%)'}),
              animate(`${ANIMATION_DURATION_MS}ms ease-out`, style({opacity: 1, transform: 'translateX(0)'})),
            ],
            {optional: true}
          ),
        ]),
      ]),
      transition(':decrement', [
        group([
          query(
            ':leave',
            [
              style({position: 'absolute', width: '100%', opacity: 1, transform: 'translateX(0)'}),
              animate(`${ANIMATION_DURATION_MS}ms ease-out`, style({opacity: 0, transform: 'translateX(100%)'})),
            ],
            {optional: true}
          ),
          query(
            ':enter',
            [
              style({opacity: 0, transform: 'translateX(-100%)'}),
              animate(`${ANIMATION_DURATION_MS}ms ease-out`, style({opacity: 1, transform: 'translateX(0)'})),
            ],
            {optional: true}
          ),
        ]),
      ]),
    ]),
  ],
})
export class ChallengeBuilderComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private store$ = inject(Store);

  @ViewChild(CbGoalComponent) cbGoalComponent?: CbGoalComponent;
  @ViewChild('stepperContainer') stepperContainer?: ElementRef<HTMLElement>;
  private backLoadingSubscription?: Subscription;

  // The following is the project (challenge) object as it is updated on each step
  cbProject$: Observable<ChallengeBuilderProject> = this.store$.pipe(select(selectChallengeBuilderProject));
  loading$: Observable<boolean> = this.store$.pipe(select(selectChallengeBuilderLoading));
  challengeNextValid$: Observable<boolean> = this.store$.pipe(select(selectChallengeNextValid));
  nextLoading$: Observable<boolean> = this.store$.pipe(select(selectChallengeBuilderNextLoading));
  backLoading$: Observable<boolean> = this.store$.pipe(select(selectChallengeBuilderBackLoading));

  // Sync step with URL parameter and compute stepIndex from step name
  private routeParams = toSignal(this.route.paramMap, {requireSync: true});
  private cbProjectSignal = toSignal(this.cbProject$);
  stepIndex = computed(() => {
    const stepParam = this.routeParams()?.get('step') as ChallengeBuilderSteps;
    if (!stepParam) return 0;
    if (stepParam === ChallengeBuilderSteps.OVERVIEW) return -1;
    const index = this.steps().indexOf(stepParam as ChallengeStep);
    return index >= 0 ? index : 0;
  });

  drawerOpen = signal(false);
  animationsDisabled = signal(true);
  projectNotFound = signal(false);
  loadingAiToggle = signal(false);

  // Step-click navigation: save current step before jumping
  pendingStepNavigation = signal<ChallengeStep | null>(null);
  loadingStepIndex = signal<number | null>(null);

  // Subjects for child components to subscribe to next/back button clicks
  nextClick$ = new Subject<void>();
  backClick$ = new Subject<void>();

  readonly challengeBuilderData = CHALLENGE_BUILDER_STEP_MAP;
  readonly stepMap = CHALLENGE_BUILDER_STEP_MAP;
  readonly defaultStepColor = '#efede7';
  readonly animationDuration = ANIMATION_DURATION_MS;

  // Track which step index is currently animating (for bounce effect)
  animatingStepIndex = signal<number | null>(null);
  reverseAnimatingStepIndex = signal<number | null>(null);
  private previousStepIndex = 0;

  readonly steps = signal(CHALLENGE_BUILDER_STEPS);

  // Steps without 'finish' for back/next navigation
  readonly navigableSteps = this.steps().filter(step => step !== 'finish');

  currentStep = computed(() => {
    if (this.stepIndex() === -1) return 'overview';
    return this.steps()[this.stepIndex()];
  });
  currentStepData = computed(() => this.challengeBuilderData[this.currentStep()]);

  maxReachableIndex = computed(() => {
    const cb = this.cbProjectSignal()?.challengeBuilder;
    if (!cb) return 0;
    if (!cb.initialGoal || !cb.goals?.length) return 0;
    if (!cb.objectives?.length) return 1;
    if (!cb.tasks?.length) return 2;
    if (!cb.metrics?.length) return 3;
    if (!cb.targets?.length) return 4;
    if (!cb.tasks.some(task => !!task.schedule?.length)) return 5;
    return 6;
  });

  isStepDisabled(index: number): boolean {
    return index > this.maxReachableIndex();
  }

  constructor() {
    // Watch for step changes and trigger animation
    effect(
      () => {
        const currentIndex = this.stepIndex();
        if (currentIndex > this.previousStepIndex) {
          // Moving forward - animate the newly active step
          this.animatingStepIndex.set(currentIndex);
          setTimeout(() => this.animatingStepIndex.set(null), this.animationDuration);
        } else if (currentIndex < this.previousStepIndex) {
          // Moving backward - reverse animate the step being deactivated
          this.reverseAnimatingStepIndex.set(this.previousStepIndex);
          setTimeout(() => this.reverseAnimatingStepIndex.set(null), this.animationDuration);
        }
        this.previousStepIndex = currentIndex;

        // Auto-scroll stepper to keep active step in view
        this.scrollToActiveStep(currentIndex);
      },
      {allowSignalWrites: true}
    );
  }

  private scrollToActiveStep(stepIndex: number): void {
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      const container = this.stepperContainer?.nativeElement;
      if (!container) return;

      const stepElements = container.querySelectorAll('.step-wrapper');
      const activeStep = stepElements[stepIndex] as HTMLElement;
      if (!activeStep) return;

      const containerRect = container.getBoundingClientRect();
      const stepRect = activeStep.getBoundingClientRect();

      // Check if step is outside the visible area
      const isOutOfViewLeft = stepRect.left < containerRect.left;
      const isOutOfViewRight = stepRect.right > containerRect.right;

      if (isOutOfViewLeft || isOutOfViewRight) {
        // Scroll to center the active step
        const scrollLeft = activeStep.offsetLeft - container.clientWidth / 2 + activeStep.offsetWidth / 2;
        container.scrollTo({left: scrollLeft, behavior: 'smooth'});
      }
    }, 0);
  }

  ngOnInit(): void {
    const projectId = this.route.snapshot.paramMap.get('id');
    this.store$.dispatch(new SetChallengeBuilderLoadingAction({loading: true}));
    this.store$.dispatch(
      new GetChallengeBuilderProjectAction({
        projectId,
        onSuccess: fetchedProject => this.getInitialStep(fetchedProject),
        onFailure: () => this.onProjectNotFound(),
      })
    );

    // Clear step-click loading state on save failure
    // (on success, onBack() clears it before this fires)
    this.backLoadingSubscription = this.backLoading$.subscribe(loading => {
      if (!loading && this.pendingStepNavigation()) {
        this.pendingStepNavigation.set(null);
        this.loadingStepIndex.set(null);
      }
    });
  }

  ngOnDestroy(): void {
    this.backLoadingSubscription?.unsubscribe();
    this.store$.dispatch(new GetProjectsAction({}));
  }

  private onProjectNotFound(): void {
    this.projectNotFound.set(true);
    this.store$.dispatch(new SetChallengeBuilderLoadingAction({loading: false}));
  }

  onBackToProjects(): void {
    this.router.navigate(['projects']);
  }

  private getInitialStep(cbProject: ChallengeBuilderProject): void {
    const navigateToStepName = this.getStepName(cbProject);
    this.router.navigate(['projects', 'challenge-builder', cbProject._id, navigateToStepName]).then(() => {
      // Enable animations after the initial navigation completes
      setTimeout(() => this.animationsDisabled.set(false), 0);
    });
  }

  private getStepName(cbProject: ChallengeBuilderProject): ChallengeBuilderSteps {
    const cb = cbProject.challengeBuilder;

    if (cb.salvages && cb.alerts && cb.rewards) {
      return ChallengeBuilderSteps.OVERVIEW;
    }
    if (cb.tasks?.length && cb.tasks?.some(task => !!task.schedule?.length)) {
      return ChallengeBuilderSteps.SCHEDULE;
    }
    if (cb.targets?.length) {
      return ChallengeBuilderSteps.TARGETS;
    }
    if (cb.metrics?.length) {
      return ChallengeBuilderSteps.METRICS;
    }
    if (cb.tasks?.length) {
      return ChallengeBuilderSteps.TASKS;
    }
    if (cb.objectives?.length) {
      return ChallengeBuilderSteps.OBJECTIVES;
    }
    if (!cb?.initialGoal || !cb?.goals?.length) {
      return ChallengeBuilderSteps.GOAL;
    }
    return ChallengeBuilderSteps.GOAL;
  }

  onStepClick(step: ChallengeStep): void {
    const stepIdx = this.steps().indexOf(step);
    if (stepIdx > this.maxReachableIndex()) return;
    if (step === this.currentStep()) return;
    if (this.loadingStepIndex() !== null) return;

    // Save current step before navigating
    this.pendingStepNavigation.set(step);
    this.loadingStepIndex.set(stepIdx);
    this.store$.dispatch(new SetBackLoadingAction({backLoading: true}));
    this.backClick$.next();
  }

  onBackClick(): void {
    if (this.currentStep() === 'goal') {
      this.cbProject$.pipe(take(1)).subscribe(c => {
        if (c.challengeBuilder.rewards && c.challengeBuilder.alerts && c.challengeBuilder.salvages) {
          this.router.navigate(['projects', 'challenge-builder', c._id, 'overview']);
        } else {
          this.router.navigate(['projects']);
        }
        return;
      });
    }
    this.store$.dispatch(new SetBackLoadingAction({backLoading: true}));
    this.backClick$.next();
  }

  onBack(): void {
    const pendingStep = this.pendingStepNavigation();
    this.pendingStepNavigation.set(null);
    this.loadingStepIndex.set(null);

    this.cbProject$.pipe(take(1)).subscribe(cbProject => {
      if (pendingStep) {
        const urlSegment = pendingStep === 'finish' ? 'overview' : pendingStep;
        this.router.navigate(['projects', 'challenge-builder', cbProject?._id, urlSegment]);
      } else {
        const prevStep = this.navigableSteps[this.stepIndex() - 1];
        this.router.navigate(['projects', 'challenge-builder', cbProject?._id, prevStep]);
      }
      this.onScrollToTop();
    });
  }

  onNextClick(): void {
    this.store$.dispatch(new SetNextLoadingAction({nextLoading: true}));

    // Emit event for child components to react to
    this.nextClick$.next();
  }

  onNext() {
    this.cbProject$.pipe(take(1)).subscribe(cbProject => {
      this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
      const nextStepIndex = this.stepIndex() + 1;
      const nextStep = this.navigableSteps[nextStepIndex];
      const urlSegment = nextStep ? nextStep : 'overview';

      // Trigger animation for the next step
      this.animatingStepIndex.set(nextStepIndex);
      setTimeout(() => this.animatingStepIndex.set(null), this.animationDuration);

      this.router.navigate(['projects', 'challenge-builder', cbProject?._id, urlSegment]);
      this.onScrollToTop();
    });
  }

  onToggleAi(): void {
    this.cbProject$.pipe(take(1)).subscribe(cbProject => {
      this.loadingAiToggle.set(true);
      this.store$.dispatch(
        new ToggleAiAction({
          projectId: cbProject._id,
          onSuccess: () => this.loadingAiToggle.set(false),
          onFailure: () => this.loadingAiToggle.set(false),
        })
      );
    });
  }

  onScrollToTop(): void {
    document.querySelector('.oph-feature-layout')?.scrollTo({top: 0, behavior: 'instant'}); // smooth
  }

  openDrawer(): void {
    this.drawerOpen.set(true);
  }

  closeDrawer(): void {
    this.drawerOpen.set(false);
  }
}
